import { useState } from "react";
import BottomSheet from "./BottomSheet";
import EmojiPicker from "./EmojiPicker";
import { AVATARS } from "../lib/constants";
import { api, formatApiError, buildInviteUrl } from "../lib/api";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";

export default function AddMemberModal({ open, onClose, groupId, onAdded }) {
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(AVATARS[0]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setName("");
    setAvatar(AVATARS[0]);
    setIsAdmin(false);
    setInviteUrl("");
    setCopied(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Please enter a name");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/members`, {
        name: name.trim(),
        avatar_emoji: avatar,
        is_admin: isAdmin,
      });
      const url = buildInviteUrl(data.invite_link_path);
      setInviteUrl(url);
      onAdded && onAdded(data.member);
      toast.success(`${name.trim()} added — copy their invite link`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to add member"));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — long-press the link to copy manually");
    }
  };

  return (
    <BottomSheet open={open} onClose={handleClose} title="Add new member" testId="add-member-modal">
      {!inviteUrl ? (
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Alex Chen"
              className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1971c2] focus:ring-2 focus:ring-[#1971c2]/20 font-medium text-base transition-colors"
              data-testid="add-member-name-input"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
              Pick an avatar
            </label>
            <EmojiPicker value={avatar} onChange={setAvatar} testIdPrefix="add-member-avatar" />
          </div>

          <label className="flex items-center gap-3 bg-[#f8f9fa] rounded-xl p-3 cursor-pointer active:scale-[0.99] transition-transform">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
              className="w-5 h-5 accent-[#1971c2]"
              data-testid="add-member-admin-checkbox"
            />
            <div>
              <div className="font-bold text-sm">Make co-admin</div>
              <div className="text-xs text-[#6c757d]">They can manage members & settings.</div>
            </div>
          </label>

          <button
            onClick={submit}
            disabled={submitting}
            className="w-full bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold text-base rounded-xl py-3.5 active:scale-95 transition-transform shadow-md disabled:opacity-60"
            data-testid="add-member-submit"
          >
            {submitting ? "Creating…" : "Generate invite link"}
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="bg-[#e6f7eb] border border-[#2f9e44]/20 rounded-2xl p-4 text-center">
            <div className="text-4xl mb-2">🎉</div>
            <div className="font-display font-extrabold text-lg text-[#212529]">
              {name} is in!
            </div>
            <div className="text-sm text-[#6c757d] mt-1">
              Send them their private invite link below.
            </div>
          </div>

          <div className="bg-[#f8f9fa] rounded-xl p-3 break-all text-xs font-mono text-[#212529]" data-testid="new-member-invite-url">
            {inviteUrl}
          </div>

          <button
            onClick={copy}
            className="w-full bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold text-base rounded-xl py-3.5 active:scale-95 transition-transform shadow-md flex items-center justify-center gap-2"
            data-testid="copy-new-member-invite"
          >
            {copied ? <Check className="w-5 h-5" strokeWidth={3} /> : <Copy className="w-5 h-5" strokeWidth={2.5} />}
            {copied ? "Copied!" : "Copy invite link"}
          </button>

          <button
            onClick={handleClose}
            className="w-full bg-white border-2 border-[#e9ecef] text-[#212529] font-bold rounded-xl py-3 active:scale-95 transition-transform"
            data-testid="add-member-done"
          >
            Done
          </button>
        </div>
      )}
    </BottomSheet>
  );
}
